import { useState, useCallback, useEffect } from 'react';
import tripActivityService from '../../../services/trips/trip-activity.service'; // <-- import the service

export interface IEditActivityModel {
  id: string | null | undefined; // Use null for new activities
  tripId: string;
  title: string;
  description: string;
  websiteUrl?: string;
  isPublic: boolean;
  imageUrl: string;
  notes?: string[];
  newNote?: string;
  customization?: {
    color?: string;
    icon?: string;
  } | null;
}

export const useEditActivity = (
  activityId: string,
  tripId: string,
  destinationId: string | undefined,
  onSuccess: () => void
) => {
  const [activity, setActivity] = useState<IEditActivityModel | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [validated] = useState(true);

  // Fetch the activity details when the hook is initialized
  useEffect(() => {
    if (activityId && tripId) {
      tripActivityService
        .getActivityById(tripId, activityId, destinationId)
        .then((data) => {
          setActivity({
            id: data.id,
            tripId: tripId,
            title: data.title,
            description: data.description ?? '',
            websiteUrl: data.websiteUrl,
            isPublic: false,
            imageUrl: data.imageUrl || 'default.png',
            notes: data.notes,
            newNote: ''
          });
          setImageUrl(data.imageUrl || '');
        })
        .catch((error) => {
          console.error('Error fetching activity:', error);
        });
    }
  }, [activityId, tripId, destinationId]);

  const handleInputChange = useCallback(
    (field: keyof IEditActivityModel, value: any) => {
      setActivity((prev) => ({
        ...prev!,
        [field]: value
      }));
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!activity) {
      console.error('No activity to update');
      return;
    }

    const editActivityModel: IEditActivityModel = {
      ...activity,
      imageUrl: imageUrl || 'default.png'
    };

    try {
      const success = await tripActivityService.updateActivity(
        tripId,
        activityId,
        editActivityModel,
        destinationId
      );
      if (success) {
        onSuccess();
      } else {
        alert('Activity update failed');
      }
    } catch (error) {
      console.error('Error updating activity:', error);
    }
  };

  return {
    activity,
    validated,
    handleInputChange,
    handleSubmit
  };
};
